/**
 * Reader-study assignment lookup.
 *
 * Calls `GET /api/study/assignment/{schedule_id}` (a2zrai/web-demo-backend#338)
 * to fetch the rad's pre-randomized schedule. The case-viewer uses the
 * result to determine the arm (`with_ai` / `without_ai`) for the currently
 * open study.
 *
 * Backend returns the raw DDB items with snake_case keys; this hook maps
 * to camelCase at the boundary so downstream React code stays consistent.
 */
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import type { StudyArm, StudyCohort } from "./useStudyEventLog";
import { getApiBaseUrl } from "../config/api";
import { useUserId } from "../context/AuthContext";

export interface StudyAssignmentRow {
  scheduleId: string;
  studyUid: string;
  arm: StudyArm;
  cohort: StudyCohort;
  sessionOrder: 1 | 2;
  /** ISO-8601. Cases in session 2 are locked until this timestamp. */
  earliestOpen: string;
  /**
   * True when this row is a practice case. Practice reports, timings, and
   * ratings are excluded from the study dataset — the study event log emits
   * no POST when ctx.isPractice is true (enforced in signStudy / logEvent via
   * the StudyContext.isPractice flag).
   *
   * Backend: the schedule generator stamps `is_practice: true` on practice
   * rows. Missing → false (all existing rows are real study cases).
   */
  isPractice: boolean;
}

export const studyAssignmentKeys = {
  all: ["study-assignment"] as const,
  schedule: (userId: string | null, scheduleId: string | null) =>
    [...studyAssignmentKeys.all, userId ?? "", scheduleId ?? ""] as const,
};

interface RawAssignmentItem {
  schedule_id: string;
  study_uid: string;
  arm: StudyArm;
  cohort: string;
  session_order: 1 | 2;
  earliest_open: string;
  /** Optional — absent on existing rows (treated as false). */
  is_practice?: boolean;
}

function toAssignmentRow(item: RawAssignmentItem): StudyAssignmentRow | null {
  // The BE schedule generator (a2zrai/web-demo-backend) constrains cohort to
  // {"A", "B"}, but the row is denormalized into JSON; defensive narrow here
  // drops rows with an unexpected cohort instead of crashing downstream.
  if (item.cohort !== "A" && item.cohort !== "B") {
    console.warn("[study] dropping assignment row with unexpected cohort", item);
    return null;
  }
  return {
    scheduleId: item.schedule_id,
    studyUid: item.study_uid,
    arm: item.arm,
    cohort: item.cohort,
    sessionOrder: item.session_order,
    earliestOpen: item.earliest_open,
    isPractice: item.is_practice === true,
  };
}

async function fetchStudyAssignment(scheduleId: string): Promise<StudyAssignmentRow[]> {
  const baseUrl = getApiBaseUrl();
  const encoded = encodeURIComponent(scheduleId);
  const response = await axios.get<{ assignments: RawAssignmentItem[] }>(
    `${baseUrl}/api/study/assignment/${encoded}`,
    { withCredentials: true },
  );
  return response.data.assignments
    .map(toAssignmentRow)
    .filter((row): row is StudyAssignmentRow => row !== null);
}

export function useStudyAssignment(scheduleId: string | null) {
  const userId = useUserId();
  return useQuery({
    queryKey: studyAssignmentKeys.schedule(userId, scheduleId),
    queryFn: () => fetchStudyAssignment(scheduleId!),
    enabled: !!scheduleId && !!userId,
    // Schedules don't change once written — long stale time is safe and
    // means the per-rad assignment lookup runs once per session.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

/**
 * Pure helper — find the assignment row for a specific study, or null if
 * the rad isn't assigned that study.
 */
export function findAssignmentForStudy(
  rows: StudyAssignmentRow[] | undefined,
  studyUid: string,
): StudyAssignmentRow | null {
  if (!rows) return null;
  return rows.find((r) => r.studyUid === studyUid) ?? null;
}
